import React from "react";
import { Typography } from "@material-ui/core";

const CODE_PATTERN = /^[A-Z][A-Z0-9_:-]+$/;

const LEGACY_MESSAGES = {
  CARBON_IMPORT_ACTIVITY_SOURCE_NOT_FOUND: {
    cause:
      "An activity references an emission source that could not be found.",
    message:
      "Check that the activity source name and site exactly match a source in the emission sources sheet.",
  },
  CARBON_IMPORT_ACTIVITY_SITE_NOT_FOUND: {
    cause: "An activity references a site that could not be found.",
    message:
      "Check that the site name or ID exactly matches a site in the Sites sheet.",
  },
  CARBON_IMPORT_HAS_REJECTED_ROWS: {
    cause: "The workbook contains rows that failed validation.",
    message:
      "Correct every issue listed in the upload details and upload the workbook again.",
  },
  CARBON_IMPORT_SHEET_UNKNOWN: {
    cause: "The workbook contains an unsupported sheet.",
    message:
      "Use the current Carbon upload template without renaming or adding input sheets.",
  },
  CARBON_IMPORT_COLUMN_MISSING: {
    cause: "The workbook is missing one or more required columns.",
    message:
      "Use the current Carbon upload template and restore the required column headers.",
  },
  CARBON_IMPORT_COLUMN_UNKNOWN: {
    cause: "The workbook contains an unsupported column.",
    message:
      "Remove renamed or extra columns, or copy the data into the current Carbon upload template.",
  },
};

const strings = (value) =>
  (Array.isArray(value) ? value : [value]).filter(
    (item) => typeof item === "string" && item.trim(),
  );

const normalizeIssue = (issue) => {
  if (!issue || typeof issue !== "object" || Array.isArray(issue)) return null;
  return {
    ...(typeof issue.sheet === "string" ? { sheet: issue.sheet } : {}),
    ...(Number.isInteger(issue.row) ? { row: issue.row } : {}),
    ...(typeof issue.column === "string" && issue.column.trim()
      ? { column: issue.column }
      : {}),
    ...(typeof issue.entityKind === "string"
      ? { entityKind: issue.entityKind }
      : {}),
    ...(typeof issue.field === "string" ? { field: issue.field } : {}),
    ...(["string", "number", "boolean"].includes(typeof issue.value)
      ? { value: String(issue.value) }
      : {}),
    message:
      typeof issue.message === "string" && issue.message.trim()
        ? issue.message
        : "Correct the affected workbook value and upload the file again.",
    ...(typeof issue.resolution === "string" && issue.resolution.trim()
      ? { resolution: issue.resolution }
      : {}),
  };
};

export const normalizeCarbonUploadError = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  if (
    ![value.code, value.causeCode, value.cause].some(
      (item) => typeof item === "string" && item.startsWith("CARBON_IMPORT_"),
    )
  )
    return null;

  const legacyCauseCode =
    typeof value.cause === "string" && CODE_PATTERN.test(value.cause)
      ? value.cause
      : undefined;
  const causeCode = value.causeCode || legacyCauseCode;
  const fallback = LEGACY_MESSAGES[causeCode];
  const cause =
    typeof value.cause === "string" && !CODE_PATTERN.test(value.cause)
      ? value.cause
      : fallback?.cause || "The Carbon import could not be completed.";
  const messages = strings(value.messages).filter(
    (message) => !CODE_PATTERN.test(message),
  );

  return {
    ...value,
    causeCode,
    cause,
    messages:
      messages.length > 0
        ? messages
        : [
            fallback?.message ||
              "Retry the upload and contact support with the technical code if it fails again.",
          ],
    issues: Array.isArray(value.issues)
      ? value.issues.map(normalizeIssue).filter(Boolean)
      : [],
  };
};

const location = (issue) =>
  [
    issue.sheet,
    Number.isInteger(issue.row) ? `row ${issue.row}` : null,
    issue.column ? `column ${issue.column}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

export const CarbonUploadErrorSummary = ({ error }) => {
  const details = normalizeCarbonUploadError(error);
  if (!details) return null;

  return (
    <div aria-label="Carbon import error summary">
      <Typography variant="h6">What went wrong</Typography>
      <Typography paragraph>{details.cause}</Typography>

      {details.issues.length > 0 ? (
        <>
          <Typography variant="subtitle1">Issues to correct</Typography>
          {details.issues.map((issue, index) => (
            <div
              key={`${issue.sheet || "issue"}-${issue.row || index}-${index}`}
              style={{
                borderLeft: "3px solid #d32f2f",
                margin: "12px 0",
                padding: "4px 12px",
              }}
            >
              {location(issue) && (
                <Typography variant="subtitle2">{location(issue)}</Typography>
              )}
              {issue.field && (
                <Typography variant="body2">
                  {issue.field}: {String(issue.value ?? "(blank)")}
                </Typography>
              )}
              <Typography>{issue.message}</Typography>
              {issue.resolution && (
                <Typography variant="body2" color="textSecondary">
                  {issue.resolution}
                </Typography>
              )}
            </div>
          ))}
        </>
      ) : (
        details.messages.map((message) => (
          <Typography paragraph key={message}>
            {message}
          </Typography>
        ))
      )}

      <Typography variant="caption" color="textSecondary">
        Technical code: {details.causeCode || details.code}
      </Typography>
    </div>
  );
};
